import React from 'react';
import { useRecoilValue } from 'recoil';
import { CardBody } from '../../components/CardBody';
import { CardFooter } from '../../components/CardFooter';

import { isMobileState } from '../../recoil/selectors';
import { HeroCallout } from '../HeroCallout';
import { WistiaPanel } from '../../components/WistiaPanel';
import { NewsIcons } from '../../product_detail/components/NewsIcons';
import { ShopAllColors } from '../components/ShopAllColors';
import { ProductFeatures } from '../components/ProductFeatures';
import { WaistHeroCincherLanderMobile } from './WaistHeroCincherLanderMobile';
import { LanderTopHero } from '../components/LanderTopHero';
import { allProductsState } from '../../recoil/atoms';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { useProductAssets } from '../../hooks/useProductAssets';
import { ProductComparison } from '../components/ProductComparison';
import { useLanderControls } from '../LanderContext';
import { assetPathFn } from '../../util';
import styles from './WaistHeroCincher.module.scss';

export const WaistHeroCincherLander: React.FC = () => {
  const allProducts = useRecoilValue(allProductsState);
  const isMobile = useRecoilValue(isMobileState);

  const product = allProducts[ProductHandle.WaistHeroCincher];
  const productAssets = useProductAssets(product);
  const wistiaVideoId = productAssets.videos[0]?.wistiaId;
  const padding = { padding: '56.25% 0 0 0' };
  const { scrollToProductSection } = useLanderControls();

  if (isMobile) {
    return <WaistHeroCincherLanderMobile />;
  }

  return (
    <>
      <CardBody>
        <LanderTopHero
          maxHeroContainerHeight={'1058px'}
          heroImage={'desktop-waisthero-cincher-top-hero.jpg'}
          title={'WaistHero Cincher'}
          titleElement={
            <>
              WaistHero
              <br /> Cincher
            </>
          }
          subTitle={
            <>
              Waist definition <br /> perfected.
            </>
          }
        >
          <HeroCallout
            orderIndex={0}
            label={<>Waist sculpting!</>}
            inlineStyles={{
              desktop: {
                left: `12%`,
                top: `32%`,
                flexDirection: 'row-reverse',
              },
            }}
          />
          <HeroCallout
            orderIndex={2}
            label={
              <>
                Strong <br /> compression
              </>
            }
            titleStyles={{ textAlign: 'center', marginTop: 6 }}
            inlineStyles={{
              desktop: {
                left: `15%`,
                top: `44%`,
                flexDirection: 'column-reverse',
              },
            }}
          />
        </LanderTopHero>
        <NewsIcons />
        <ProductFeatures
          features={[
            {
              imgName: 'feature-whc-desktop-1.jpg',
              title: '3 levels of sculpting',
              description: 'Adjust your level of sculpting to match your body’s fluctuations and desired level of compression.',
            },
            {
              imgName: 'feature-whc-desktop-2.jpg',
              title: 'Easy on and off',
              description: 'Zip in and out easily thanks to our unique hook and zip system with skin-protecting fabric barrier.',
            },
            {
              imgName: 'feature-whc-desktop-3.jpg',
              title: 'Powerful and comfortable',
              description: 'We’ve combined the power of corsetry with the ease of stretch to maximize effectiveness and comfort.',
            },
          ]}
        />
        <ProductComparison
          onCtaClick={scrollToProductSection}
          competitorImage="whc-competitor.jpg"
          honeyloveImage="whc-honeylove.jpg"
          honeyloveText="Honeylove"
          competitorText="Competitor"
          description={'We spent three years perfecting the WaistHero Cincher to ensure it offers maximum waist sculpting, comfort, and ease of wear.'}
          title={
            <>
              See and feel <br /> the difference.
            </>
          }
        />
        <div className={styles.wearUnder}>
          <img src={assetPathFn('whc-promo-last.jpg')} alt="fits perfectly from xs-3x" />
          <div style={{ flex: 1 }}>
            <div className={styles.wearUnderText}>
              Wear under or <br /> over clothes.
            </div>
          </div>
        </div>
        {wistiaVideoId && (
          <div style={{ padding: '50px 60px', paddingTop: 40, boxSizing: 'border-box' }}>
            <WistiaPanel videoId={wistiaVideoId} responsivePadding={padding} />
          </div>
        )}
        <ShopAllColors product={allProducts[ProductHandle.WaistHeroCincher]} />
      </CardBody>
      <CardFooter />
    </>
  );
};
